import React, { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { isWebView } from '../../../../utils/navigation'
import { useGetFinalParsedValidationSchema } from '../../../../services/validation/validationService'
import { getRequiredFieldsPerScreen } from '../../../../services/validation/requiredFieldsUtils/getRequiredFieldsPerScreen'
import { ScreenName } from '../../../../services/domain/wizardScreens'
import { useAppSelector } from '../../../../app/hooks'

interface MandatoryHintProps {
  screenNames?: ScreenName[]
  showMandatoryHint?: boolean
  className?: string
  iconClassName?: string
}

export const MandatoryHintWrapper: FC<MandatoryHintProps> = ({
  screenNames,
  showMandatoryHint,
  className,
  iconClassName,
}) => {
  const finalParsedValidationSchema = useGetFinalParsedValidationSchema()
  const isWebViewMode = isWebView()
  const isWaitingListFlow = useAppSelector((state) => state.flowFeature.flowInformation.options?.isWaitingListFlow)
  const screenHasMandatoryField = useMemo(() => {
    if (!screenNames?.length) return false

    if (isWebViewMode) {
      // WebView: check if ANY screen has mandatory fields
      return screenNames.some(
        (screenName) => getRequiredFieldsPerScreen(finalParsedValidationSchema, screenName).length > 0
      )
    } else {
      // Desktop: check only first screen because only one screen is visible
      return getRequiredFieldsPerScreen(finalParsedValidationSchema, screenNames[0]).length > 0
    }
  }, [finalParsedValidationSchema, screenNames, isWebViewMode])

  const noScreensProvided = !screenNames || screenNames.length === 0
  const hasRequiredFieldsAndShouldShow = screenHasMandatoryField && showMandatoryHint
  const shouldShowByDefault = showMandatoryHint && noScreensProvided

  const shouldRender = shouldShowByDefault || hasRequiredFieldsAndShouldShow

  if (!shouldRender) return null

  return (
    <div
      className={`grid-item one-whole ${className?.includes('gutter') ? '' : 'margin-top-l'}`}
      data-testid='mandatory-hint-container'
    >
      <MandatoryHint isWebViewMode={isWebViewMode} className={iconClassName} isWaitingListFlow={isWaitingListFlow} />
    </div>
  )
}

interface MandatoryHintTextProps {
  isWebViewMode: boolean
  isWaitingListFlow?: boolean
  className?: string
}

const MandatoryHint: FC<MandatoryHintTextProps> = ({ isWebViewMode, className, isWaitingListFlow }) => {
  const { t } = useTranslation()

  const cssClasses = className
    ? className
    : isWebViewMode
      ? 'font-gray font-body-medium-regular float-left margin-bottom-l'
      : 'font-lightgray font-body-small-regular float-right'

  const translationKey = isWebViewMode || isWaitingListFlow ? 'message.mobile.mandatory' : 'message.mandatory'

  return <span className={cssClasses}>{t(translationKey)}</span>
}
